/**
 * AdminLayout template - standard layout for admin pages
 * Provides consistent structure for all admin pages
 */

import React from 'react';
import { Box, Container, VStack, useColorModeValue } from '@chakra-ui/react';
import {
  PageHeader,
  PageHeaderProps,
} from '../../organisms/headers/PageHeader';
import { BaseComponentProps } from '../../types';

export interface AdminLayoutProps extends BaseComponentProps {
  /** Page header configuration */
  header?: PageHeaderProps;
  /** Main content */
  children: React.ReactNode;
  /** Whether to use container wrapper */
  containerized?: boolean;
  /** Maximum width for containerized layout */
  maxWidth?: string;
  /** Additional padding */
  padding?: number;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({
  header,
  children,
  containerized = true,
  maxWidth = '1200px',
  padding = 8,
  className,
  testId,
}) => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  const content = (
    <VStack spacing={8} align="stretch" width="full">
      {header && <PageHeader {...header} />}
      <Box flex="1">{children}</Box>
    </VStack>
  );

  return (
    <Box
      minHeight="100vh"
      bg={bgColor}
      className={className}
      data-testid={testId}
    >
      {containerized ? (
        <Container maxW={maxWidth} py={padding} height="full">
          {content}
        </Container>
      ) : (
        <Box p={padding} height="full">
          {content}
        </Box>
      )}
    </Box>
  );
};
